import { dateTime, DateTimeFormats } from '../../../time/datetime';
import { LoggedInUser } from '../../../state/helperServices/auth/auth.interfaces';
import { Player } from '../../../state/reduxRepositories/players/player.interfaces';

// All known Docto gender designations
export enum DoctoBookingSystemSex {
  male = 'male',
  female = 'female',
  other = 'other',
}

// All known Docto booking-for options
export enum DoctoBookingSystemAppointmentFor {
  patient = 'patient',
  familyMember = 'familyMember',
}

// All known Docto query string params
export enum DoctoBookingSystemParamNames {
  email = 'email',
  firstName = 'firstName',
  lastName = 'lastName',
  playerId = 'playerId',
  sex = 'sex',
  dob = 'dob',
  phone = 'phone',
  countryCode = 'countryCode',
  postcode = 'postcode',
  address = 'address',
  country = 'country',
  city = 'city',
  medicareNumber = 'medicareNumber',
  medicareIrn = 'medicareIrn',
  appointmentFor = 'appointmentFor',
  familyMember_firstName = 'familyMember_firstName',
  familyMember_lastName = 'familyMember_lastName',
  familyMember_sex = 'familyMember_sex',
  familyMember_dob = 'familyMember_dob',
  familyMember_medicareNumber = 'familyMember_medicareNumber',
  familyMember_medicareIrn = 'familyMember_medicareIrn',
}

export const addBookingSystemQueryParam = (
  queryString: string,
  paramName: string,
  paramValue: string,
): string => {
  return `${queryString}&${paramName}=${paramValue}`;
};

export const formatDateForDoctoBookingSystem = (date: string) => {
  return dateTime.format(
    new Date(date),
    DateTimeFormats.DoctoBookingSystemDayMonthYear,
    undefined,
    'Australia/Brisbane',
  );
};

// This is for testing params which we don't yet have data for
export const addTestDoctoBookingSystemParams = (
  queryString: string,
): string => {
  let newQueryString = queryString;

  newQueryString = addBookingSystemQueryParam(
    newQueryString,
    DoctoBookingSystemParamNames.sex,
    DoctoBookingSystemSex.female,
  );
  newQueryString = addBookingSystemQueryParam(
    newQueryString,
    DoctoBookingSystemParamNames.familyMember_sex,
    DoctoBookingSystemSex.male,
  );
  newQueryString = addBookingSystemQueryParam(
    newQueryString,
    DoctoBookingSystemParamNames.dob,
    '01-02-1999',
  );
  newQueryString = addBookingSystemQueryParam(
    newQueryString,
    DoctoBookingSystemParamNames.phone,
    '1234567890',
  );
  newQueryString = addBookingSystemQueryParam(
    newQueryString,
    DoctoBookingSystemParamNames.postcode,
    '1234',
  );
  newQueryString = addBookingSystemQueryParam(
    newQueryString,
    DoctoBookingSystemParamNames.address,
    '1234HornbyStreet',
  );
  newQueryString = addBookingSystemQueryParam(
    newQueryString,
    DoctoBookingSystemParamNames.city,
    'NewYork',
  );
  newQueryString = addBookingSystemQueryParam(
    newQueryString,
    DoctoBookingSystemParamNames.medicareNumber,
    '1234567890',
  );
  newQueryString = addBookingSystemQueryParam(
    newQueryString,
    DoctoBookingSystemParamNames.medicareIrn,
    '1',
  );
  newQueryString = addBookingSystemQueryParam(
    newQueryString,
    DoctoBookingSystemParamNames.familyMember_medicareNumber,
    '1234567890',
  );
  newQueryString = addBookingSystemQueryParam(
    newQueryString,
    DoctoBookingSystemParamNames.familyMember_medicareIrn,
    '1',
  );

  return newQueryString;
};

export const getThirdPartyBookingSystemLink = (
  clinicPlayerId: string,
  user: LoggedInUser,
  player: Player | undefined,
): string => {
  const baseUrl: string = 'https://www.docto.com.au/companies/hitiq';
  let queryString: string = `?playerId=${clinicPlayerId}`;

  queryString = addBookingSystemQueryParam(
    queryString,
    DoctoBookingSystemParamNames.email,
    user?.email,
  );

  queryString = addBookingSystemQueryParam(
    queryString,
    DoctoBookingSystemParamNames.firstName,
    user?.firstName,
  );

  queryString = addBookingSystemQueryParam(
    queryString,
    DoctoBookingSystemParamNames.lastName,
    user?.lastName,
  );

  queryString = addBookingSystemQueryParam(
    queryString,
    DoctoBookingSystemParamNames.countryCode,
    '61',
  );

  if (user?.phone) {
    queryString = addBookingSystemQueryParam(
      queryString,
      DoctoBookingSystemParamNames.phone,
      user?.phone,
    );
  }

  queryString = addBookingSystemQueryParam(
    queryString,
    DoctoBookingSystemParamNames.country,
    'Australia',
  );

  // const player = players.find((p) => p.id === selectedPlayerId);

  // We always book for family member - it is not possible to book for yourself in your roles as a guardian without
  // creating another "player" in your own name - in which case Docto still sees this relationship as familyMember
  queryString = addBookingSystemQueryParam(
    queryString,
    DoctoBookingSystemParamNames.appointmentFor,
    DoctoBookingSystemAppointmentFor.familyMember,
  );

  if (player?.firstName) {
    queryString = addBookingSystemQueryParam(
      queryString,
      DoctoBookingSystemParamNames.familyMember_firstName,
      player?.firstName.trim(),
    );
  }

  if (player?.lastName) {
    queryString = addBookingSystemQueryParam(
      queryString,
      DoctoBookingSystemParamNames.familyMember_lastName,
      player?.lastName.trim(),
    );
  }

  if (player?.dateOfBirth) {
    queryString = addBookingSystemQueryParam(
      queryString,
      DoctoBookingSystemParamNames.familyMember_dob,
      formatDateForDoctoBookingSystem(player?.dateOfBirth),
    );
  }

  // TEST ONLY
  // This is for TESTING ONLY - parameters for which we currently lack data but may need to add in future
  // //queryString = addTestDoctoBookingSystemParams(queryString);

  return `${baseUrl}${queryString}`;
};
